import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2 } from 'lucide-react';
import { submitLead, handleLeadResponse, countries, getGeoLocation } from '../utils/api';

const ConsultationDialog = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  // Auto-detect country on mount
  useEffect(() => {
    const detectCountry = async () => {
      const geoData = await getGeoLocation();
      if (geoData?.data?.countryCode) {
        const country = countries.find(c => c.code === geoData.data.countryCode);
        if (country) {
          setSelectedCountry(country);
        } else {
          // Default to US if not found
          setSelectedCountry(countries.find(c => c.code === 'US'));
        }
      } else {
        // Default to US
        setSelectedCountry(countries.find(c => c.code === 'US'));
      }
    };
    detectCountry();
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhoneChange = (e) => {
    // Allow only numbers
    const value = e.target.value.replace(/\D/g, '');
    setFormData((prev) => ({ ...prev, phone: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation - all fields required except message
    if (!formData.name || formData.name.length < 2) {
      toast.error('Please enter a valid name (at least 2 characters)');
      return;
    }
    if (!formData.email || !formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!formData.phone || formData.phone.length < 7) {
      toast.error('Please enter a valid phone number');
      return;
    }
    if (!selectedCountry) {
      toast.error('Please select a country');
      return;
    }

    setLoading(true);

    try {
      const leadData = {
        name: formData.name,
        email: formData.email,
        countryDialCode: selectedCountry.dialCode,
        country: selectedCountry.code,
        phoneNumber: formData.phone,
        description: formData.message,
      };

      const response = await submitLead(leadData);

      handleLeadResponse(
        response,
        () => {
          // Success callback (redirect happens in handleLeadResponse)
          toast.success('Consultation request submitted successfully!');
        },
        (errorMsg) => {
          // Error callback
          toast.error(errorMsg);
          setLoading(false);
        }
      );

      // If we reach here and response was successful, reset form
      if (response.success) {
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
        });
      }
    } catch (error) {
      console.error('Error submitting consultation request:', error);
      toast.error('Failed to submit request. Please try again.');
      setLoading(false);
    }
  };

  if (!selectedCountry) {
    return null; // Don't render until country is detected
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto w-[95vw] p-4 sm:p-6" data-testid="consultation-dialog">
        <DialogHeader className="space-y-2">
          <DialogTitle className="text-xl sm:text-2xl font-bold text-[#0F172A] font-heading">
            Request a Free Legal Consultation
          </DialogTitle>
          <DialogDescription className="text-sm sm:text-base text-slate-600">
            Fill out the form below and one of our experienced attorneys will provide you with professional legal counsel within 24 hours.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
          {/* Name */}
          <div>
            <Label htmlFor="name" className="text-[#0F172A] font-medium text-sm sm:text-base">
              Full Name *
            </Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="John Doe"
              className="mt-1 text-sm sm:text-base"
              required
              data-testid="consultation-name-input"
            />
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="email" className="text-[#0F172A] font-medium text-sm sm:text-base">
              Email Address *
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="john.doe@example.com"
              className="mt-1 text-sm sm:text-base"
              required
              data-testid="consultation-email-input"
            />
          </div>

          {/* Country & Phone */}
          <div>
            <Label className="text-[#0F172A] font-medium text-sm sm:text-base">
              Contact Number *
            </Label>
            <div className="flex gap-2 mt-1">
              <Select
                value={selectedCountry?.code}
                onValueChange={(value) => {
                  const country = countries.find(c => c.code === value);
                  setSelectedCountry(country);
                }}
              >
                <SelectTrigger className="w-[140px] sm:w-[180px] text-sm sm:text-base" data-testid="country-select">
                  <SelectValue>
                    +{selectedCountry.dialCode} ({selectedCountry.code})
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {countries.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      +{country.dialCode} - {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={handlePhoneChange}
                placeholder="1234567890"
                className="flex-1 text-sm sm:text-base"
                required
                data-testid="consultation-phone-input"
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">Enter numbers only, no spaces or special characters</p>
          </div>

          {/* Message */}
          <div>
            <Label htmlFor="message" className="text-[#0F172A] font-medium text-sm sm:text-base">
              How Can We Help You?
            </Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              placeholder="Please describe your legal needs in detail..."
              rows={4}
              className="mt-1 text-sm sm:text-base"
              data-testid="consultation-message-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              {formData.message.length}/1000 characters
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex gap-2 sm:gap-3 pt-3 sm:pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 text-sm sm:text-base"
              disabled={loading}
              data-testid="consultation-cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-[#2563EB] hover:bg-[#1d4ed8] text-white text-sm sm:text-base"
              disabled={loading}
              data-testid="consultation-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ConsultationDialog;
